import {action, observable} from 'mobx'
import {notification} from 'antd'

import {Charts} from 'Store/charts'
import {Tables} from './tables'
import {Debug} from './debug'

export class PaperTrading {
  @observable session = {}
  @observable algo = {}
  @observable metrics = []
  @observable loadingStatuses = {}

  @observable loading = true
  @observable turningOn = false
  @observable redirecting = false

  constructor(rootStore, api) {
    this.root = rootStore
    this.api = api
    this.charts = new Charts(rootStore, 'paper-trading-sessions')
    this.tables = new Tables(api)
    this.debug = new Debug(rootStore, api)
  }

  @action async pause() {
    this.loadingStatuses.is_paused = true
    try {
      await this.api.request(`sys/paper-trading-sessions/${this.session.id}/pause`, 'post')
      this.session.is_paused = true
    } catch (e) { //eslint-disable-line no-unused
      notification['error']({
        message: 'Failed to pause'
      })
    } finally {
      this.loadingStatuses.is_paused = false
    }
  }

  @action async unpause() {
    this.loadingStatuses.is_paused = true
    try {
      await this.api.request(`sys/paper-trading-sessions/${this.session.id}/unpause`, 'post')
      this.session.is_paused = false
    } catch (e) { //eslint-disable-line no-unused
      notification['error']({
        message: 'Failed to unpause'
      })
    } finally {
      this.loadingStatuses.is_paused = false
    }
  }

  @action async stop() {
    this.loadingStatuses.is_active = true
    var {id} = this.session
    try {
      await this.api.request(`sys/paper-trading-sessions/${id}/stop`, 'post')
      this.session.end_date = new Date().toISOString()
      this.session.is_active = false
    } catch (e) { //eslint-disable-line no-unused
      notification['error']({
        message: 'Failed to stop'
      })
    } finally {
      this.loadingStatuses.is_active = false
    }
  }

  @action async turnNotificationsOn() {
    this.loadingStatuses.is_notification_on = true
    try {
      await this.api.request(`sys/paper-trading-sessions/${this.session.id}/turn-notifications-on`, 'post')
      this.session.is_notification_on = true
    } catch (e) { //eslint-disable-line no-unused
      notification['error']({
        message: 'Failed to turn notifications on'
      })
    } finally {
      this.loadingStatuses.is_notification_on = false
    }
  }

  @action async turnNotificationsOff() {
    this.loadingStatuses.is_notification_on = true
    try {
      await this.api.request(`sys/paper-trading-sessions/${this.session.id}/turn-notifications-off`, 'post')
      this.session.is_notification_on = false
    } catch (e) { //eslint-disable-line no-unused
      notification['error']({
        message: 'Failed to turn notifications off'
      })
    } finally {
      this.loadingStatuses.is_notification_on = false
    }
  }

  @action async fetchPaperTrading(id) {
    this.session = {}
    this.algo = {}
    this.loading = true
    try {
      const {algo, ...data} = await this.api.request(`sys/paper-trading-sessions/${id}`, 'get')
      this.session = data
      this.algo = algo
      if (algo) {
        this.charts.changeFrequency(algo.frequency)
      }
    } catch (e) { //eslint-disable-line no-unused
      if (e.status_code === 404) {
        this.root.redirectTo({pathname: '/not-found'})
        return
      }
      notification['error']({
        message: 'Failed to load paper trading data',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.loading = false
    }
  }

  @action async fetchMetrics(id) {
    try {
      const {metrics} = await this.api.request(`sys/paper-trading-sessions/${id}/metrics`, 'get')
      this.metrics = metrics
    } catch (e) {
      notification['error']({
        message: 'Failed to load metrics',
        description: e.message || 'Unknown error'
      })
    }
  }

  @action turnOn = async ({type = 'paper', algoId, redirect = false, password}) => {
    this.turningOn = true
    this.redirecting = redirect
    const url = `sys/algos/${algoId}/turn-${type}-trading-on`
    try {
      if (type === 'live') {
        const body = {password}
        await this.api.request(url, 'post', body)
      } else {
        await this.api.request(url, 'post')
      }

      if (redirect) {
        await this.root.algo.single.load(algoId)
        const session = this.root.algo.single.data.active_trading_session
        if (session) {
          this.root.redirectTo({
            pathname: `/manager/paper-trading/${session.id}`
          })
        }
      } else {
        const {id} = this.root.backtest.single.data
        this.root.backtest.single.load(id)
      }
    } catch (e) {
      notification['error']({
        message: `Failed to turn ${type} trading on`,
        description: e.message || 'Unknown error'
      })
    } finally {
      this.turningOn = false
      this.redirecting = false
    }
  }
}
